import { Box, Button, Heading, useDisclosure } from "@chakra-ui/react";
import {
  Card,
  CardHeader,
  CurrencyFlagIcon,
  LabeledItem,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { can } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, useContext } from "react";
import { ModalExchangeRates } from "./modals/exchange-rates/ModalExchangeRates";
import type { ActiveExchangeRateModel } from "./models";
import { PageClientDetailCtx } from "./PageClientDetail";

const SectionCurrency: FunctionComponent<
  React.PropsWithChildren<{
    workspaceCurrencySymbol: string;
    currency: ActiveExchangeRateModel;
  }>
> = observer(function SectionCurrency({
  currency: i,
  workspaceCurrencySymbol,
}) {
  return (
    <Card.Subsection>
      <div className="flex items-center">
        <span className="mr-4">
          <CurrencyFlagIcon code={i.currency.code} />
        </span>
        <span className="font-bold">{i.currency.code}</span>:{" 1 "}
        {workspaceCurrencySymbol} {" = "}
        {currency.formatByNumberModel(i.rate, i.currency.code)}
      </div>
    </Card.Subsection>
  );
});

export const CardExchangeRates: FunctionComponent<
  React.PropsWithChildren<object>
> = observer(function CardExchangeRates() {
  const { workspaceStore } = useStore();
  const store = useContext(PageClientDetailCtx);
  const { activeRates } = store;

  const showRatesModal = useDisclosure();

  return (
    <Box>
      <CardHeader
        icon="globe-01"
        title={
          <Heading size="md">
            <Trans>Exchange rates</Trans>
          </Heading>
        }
        action={
          can("brand_update") && (
            <Button
              colorScheme="grey"
              leftIcon={<Icon name="plus" />}
              onClick={showRatesModal.onOpen}
              variant="outline"
            >
              <Trans>Edit rates</Trans>
            </Button>
          )
        }
      >
        <LabeledItem className="mt-6" label={t`System currency`}>
          <span className="mr-2">
            <CurrencyFlagIcon code={workspaceStore.settings?.currency.code!} />
          </span>
          {workspaceStore.settings?.currency.code}
        </LabeledItem>
      </CardHeader>

      {activeRates.length > 0 && (
        <Card.Section>
          {activeRates.map((i) => (
            <SectionCurrency
              currency={i}
              key={i.id}
              workspaceCurrencySymbol={workspaceStore.settings!.currency_symbol}
            />
          ))}
        </Card.Section>
      )}

      <ModalExchangeRates
        {...showRatesModal}
        title={<Trans>Exchange rates</Trans>}
      />
    </Box>
  );
});
